import {omitBy, sortBy} from "lodash";
import {safeJSONParse} from '@/utils';
import {getCountryJson} from "@/apis/payment";
import {isINTaxExempt} from '@/utils/tax';
import {
  checkABNNumber,
  checkAEBNNumber,
  checkIDBNNumber,
  checkKRBNNumber,
  checkMXBNNumber,
  checkSABNNumber,
  checkSGBNNumber,
  checkZABNNumber,
  checkINTaxData,
} from '@/utils/tax-number-validation';
import { logger } from "@/utils/log";

export const RESTRICTED_COUNTRIES = ['CU', 'IR', 'KP', 'SY', 'CN'];

export const ADDRESS_ACTION_TYPES = {
  fetchCountryMap: '/get/country/map',
}

export const CONTACT_TYPES = {
  BILLING_TO: 0,
  SOLD_TO: 1
}

export const TAX_ID_STATUS = {
  UNVERIFIED: 'unverified',
  VERIFIED: 'verified',
  FAILED: 'failed',
  STATE_MISMATCH: 'state_mismatch'
}

export const TAX_PREFIX = {
  'GR': 'EL',
  'MC': 'FR',
  'IM': 'GB'
}

export const NO_PREFIX_COUNTRY = ['GR', 'LI', 'MC', 'IM', 'SG', 'AU', 'RU', 'IN'];

// Used for country like 'Monaco'
export const COUNTRY_REPLACEMENT = {
  'MC': 'FR',
  'IM': 'GB',
  'IC': 'ES'
};

export const TAX_TYPE = {
  INPUT: 'input',
  DROPDOWN: 'dropdown'
};

export const taxTermGetter = ($t, region) => {
  const TaxTermMap = {
    "GB": $t('billing.tax_term.vat'),
    "NL": $t('billing.tax_term.vat'),
    "DE": $t('billing.tax_term.vat'),
    "AU": $t('billing.tax_term.gst'),
    "IN": $t('billing.tax_term.gst'),
    "SG": $t('billing.tax_term.gst'),
    "IC": $t('billing.tax_term.igic'),
    "CL": $t('billing.tax_term.iva'),
    "CO": $t('billing.tax_term.iva'),
    "MX": $t('billing.tax_term.iva'),
    "JP": $t('billing.tax_term.jct'),
    "MY": $t('billing.tax_term.stods'),
    "AM": $t('billing.tax_term.vat'),
    "AT": $t('billing.tax_term.vat'),
    "BH": $t('billing.tax_term.vat'),
    "BB": $t('billing.tax_term.vat'),
    "BD": $t('billing.tax_term.vat'),
    "BY": $t('billing.tax_term.vat'),
    "BE": $t('billing.tax_term.vat'),
    "BG": $t('billing.tax_term.vat'),
    "KH": $t('billing.tax_term.vat'),
    "CM": $t('billing.tax_term.vat'),
    "HR": $t('billing.tax_term.vat'),
    "CY": $t('billing.tax_term.vat'),
    "CZ": $t('billing.tax_term.vat'),
    "DK": $t('billing.tax_term.vat'),
    "EG": $t('billing.tax_term.vat'),
    "EE": $t('billing.tax_term.vat'),
    "FI": $t('billing.tax_term.vat'),
    "FR": $t('billing.tax_term.vat'),
    "GE": $t('billing.tax_term.vat'),
    "GR": $t('billing.tax_term.vat'),
    "HU": $t('billing.tax_term.vat'),
    "IS": $t('billing.tax_term.vat'),
    "ID": $t('billing.tax_term.vat'),
    "IE": $t('billing.tax_term.vat'),
    "IM": $t('billing.tax_term.vat'),
    "IT": $t('billing.tax_term.vat'),
    "KZ": $t('billing.tax_term.vat'),
    "KE": $t('billing.tax_term.vat'),
    "LV": $t('billing.tax_term.vat'),
    "LI": $t('billing.tax_term.vat'),
    "LT": $t('billing.tax_term.vat'),
    "LU": $t('billing.tax_term.vat'),
    "MT": $t('billing.tax_term.vat'),
    "MD": $t('billing.tax_term.vat'),
    "MC": $t('billing.tax_term.vat'),
    "NG": $t('billing.tax_term.vat'),
    "NO": $t('billing.tax_term.vat'),
    "OM": $t('billing.tax_term.vat'),
    "PH": $t('billing.tax_term.tin'),
    "PL": $t('billing.tax_term.vat'),
    "PT": $t('billing.tax_term.vat'),
    "RO": $t('billing.tax_term.vat'),
    "RU": $t('billing.tax_term.vat'),
    "SA": $t('billing.tax_term.vat'),
    "SN": $t('billing.tax_term.vat'),
    "SK": $t('billing.tax_term.vat'),
    "SI": $t('billing.tax_term.vat'),
    "ZA": $t('billing.tax_term.vat'),
    "KR": $t('billing.tax_term.vat'),
    "ES": $t('billing.tax_term.vat'),
    "SE": $t('billing.tax_term.vat'),
    "CH": $t('billing.tax_term.vat'),
    "TW": $t('billing.tax_term.vat'),
    "TZ": $t('billing.tax_term.vat'),
    "TH": $t('billing.tax_term.vat'),
    "TR": $t('billing.tax_term.vat'),
    "UG": $t('billing.tax_term.vat'),
    "UA": $t('billing.tax_term.vat'),
    "AE": $t('billing.tax_term.vat'),
    "UZ": $t('billing.tax_term.vat'),
    "VN": $t('billing.tax_term.vat')
  };
  if (!TaxTermMap[region]) {
    logger.warn(`can't find tax term!: ${region}`)
    return $t('billing.tax_term.vat')
  }
  return TaxTermMap[region]
}

export const YELLOW_COUNTRIES = [
  'AU',          'PH',
  'BD',          'IC',
  'SG',          'CL',
  'CO',          'BY',
  'KH',          'GE',
  'IS',          'KZ',
  'MD',          'NO',
  'OM',          'RU',
  'KR',          'TW',
  'TZ',          'TH',
  'TR',          'UG',
  'UA',          'VN'
]

// https://docs.google.com/spreadsheets/d/1dXE8j9GYtNHTAtP4rIPh9mOeDzPXwjwlj33YP0t0yPs/edit?gid=560084279#gid=560084279
// The following changes are not applied to the red country:
// 1. Tooltips copywriting changes
// 2. When the account type is personal, tax form is not displayed.
export const RED_SECTION_REGIONS = [
  'AM',
  'BS',
  'BB',
  'CM',
  'EG',
  'IN',
  'ID',
  'JP',
  'KE',
  'LI',
  'MY',
  'MX',
  'NG',
  'SA',
  'SN',
  'ZA',
  'CH',
  'AE',
  'UZ',
  'PE',
  'PH',
  ...YELLOW_COUNTRIES
]

export const taxConfigurationGetter = $t => ({
  'PE': {
    message: $t('billing.vat.pe_msg'),
    placeholder: 'RUC',
    checkbox: $t('billing.vat.pe_checkbox'),
    tooltip: $t('billing.vat.pe_tooltip'),
    validation: /^(10|20)\d{9}$/,
    taxTerm: taxTermGetter($t, 'PE')
  },
  'AM': {
    message: $t('billing.amMsg'),
    placeholder: $t('billing.vat_registration_placeholder'),
    checkbox: $t('billing.registered_for_vat'),
    tooltip: $t('billing.amMsgDesc'),
    validation: /^\d{8}\/1$/,
    revert: true,
    isEmptiable: true,
    ignorePrefix: true,
    taxTerm: taxTermGetter($t, 'AM')
  },
  'AU': {
    message: $t('billing.not_gst_tips_for_au'),
    prefix: 'GST',
    placeholder: $t('billing.gst_number'),
    checkbox: $t('billing.not_gst_registered'),
    tooltip: $t('billing.gst_checkbox_desc'),
    validation: checkABNNumber,
    ignorePrefix: true,
    taxTerm: taxTermGetter($t, 'AU')
  },
  'BS': {
    message: $t('billing.vat_header_val_BS'),
    placeholder: 'TIN',
    validation: /^\d{9}$/,
    ignorePrefix: true,
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'BS')
  },
  'BH': {
    message: $t('billing.vat_header_val_BH'),
    placeholder: $t('billing.vat_id_number_placeholder'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_IS_BH'),
    validation: /^\d{15}$/,
    taxTerm: taxTermGetter($t, 'BH')
  },
  'BD': {
    message: $t('billing.vat_header_val_BD'),
    placeholder: 'BIN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_BD'),
    taxTerm: taxTermGetter($t, 'BD'),
  },
  'BB': {
    message: $t('billing.vat_header_val_BB'),
    placeholder: $t('billing.vat_number'),
    validation: /^\d{13}$/,
    taxExempt: false,
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'BB'),
  },
  'SN': {
    message: $t('billing.vat_SN_message'),
    placeholder: $t('billing.vat_SN_placeholder'),
    validation: /^[0-9]{9} ?[012][a-zA-Z][0-9]$/,
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'SN'),
  },
  'BY': {
    message: $t('billing.vat.msg_by'),
    placeholder: $t('billing.tin_number'),
    checkbox: $t('billing.vat.checkbox_by'),
    tooltip: $t('billing.vat.tooltip_by'),
    validation: /^\d{9}$/,
    taxTerm: taxTermGetter($t, 'BY'),
  },
  'CO': {
    message: $t('billing.vat.msg_co'),
    placeholder: $t('billing.vat.placeholder_co'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat.tooltip_co'),
    validation: /^\d{9}-\d$/,
    taxTerm: taxTermGetter($t, 'CO'),
  },
  'KH': {
    message: $t('billing.vat_header_val_KHM'),
    placeholder: 'TIN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_KHM'),
    validation: /^[LlKk]00\d-\d{9}$|^[Ee]00\d-\d{10}$/,
    taxTerm: taxTermGetter($t, 'KH'),
  },
  'CM': {
    message: $t('billing.vat_header_val_CM'),
    placeholder: 'NIU',
    taxExempt: false,
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'CM'),
  },
  'CL': {
    message: $t('billing.tax_promote_cl'),
    placeholder: $t('billing.rut_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat.tooltip_cl'),
    validation: /^(\w{9})$/,
    ignore:/-|\s|\./g,
    taxTerm: taxTermGetter($t, 'CL'),
  },
  'EG': {
    message: $t('billing.vat.tax_promote_eg'),
    placeholder: $t('billing.vat.tax_registration_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat.tooltip_eg'),
    validation: /^\d{3}-\d{3}-\d{3}$|^\d{3}\/\d{3}\/\d{3}$|^\d{9}$/,
    uinValidation: /^[A-Za-z0-9]{39}$/,
    taxTerm: taxTermGetter($t, 'EG'),
  },
  'GE': {
    message: $t('billing.vat_header_val_GE'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message', [$t('billing.vat_checkout_popup_message_GE')]),
    validation: /^\d{9}$/,
    taxTerm: taxTermGetter($t, 'GE'),
  },
  'GH': {
    message: $t('billing.vat_header_val_GH'),
    placeholder: 'TIN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_IS_BH'),
    validation: /^[CV]\d{10}$/,
    taxTerm: taxTermGetter($t, 'GH'),
  },
  'IS': {
    message: $t('billing.vat_header_val_IS'),
    placeholder: $t('billing.vat_id_number_placeholder'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_IS_BH'),
    validation: /^\d{5}$|^\d{6}$/,
    taxTerm: taxTermGetter($t, 'IS'),
  },
  'IN': {
    message: $t('billing.inMsg'),
    placeholder: $t('billing.gst_number'),
    tooltip: $t('billing.gst_checkbox_desc_template', [$t('billing.tax_checkbox_desc_non_inr_in')]),
    validation: /^\d{2}[A-Za-z0-9]{13}$/,
    taxExempt: isINTaxExempt,
    taxDataValidation: checkINTaxData,
    isEmptiable: true,
    revert: true,
    taxTerm: taxTermGetter($t, 'IN'),
  },
  'ID': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: checkIDBNNumber,
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'ID'),
  },
  'JP': {
    message: $t('billing.tax_number_on_invoice_tips'),
    placeholder: 'JCT Number (Optional)',
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'JP'),
  },
  'KZ': {
    message: $t('billing.vat_header_val_KAZ'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message', [$t('billing.vat_checkout_popup_message_KAZ')]),
    validation: /^\d{12}$/,
    taxTerm: taxTermGetter($t, 'KZ'),
  },
  'KE': {
    message: $t('billing.keMsg'),
    placeholder: $t('billing.pin_number'),
    validation: /^[AP][0-9]{9}[A-Z]$/,
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'KE'),
  },
  'KR': {
    message: $t('billing.vat.msg_kr'),
    prefix: 'KR',
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_cat2_checkbox_desc'),
    validation: checkKRBNNumber,
    taxTerm: taxTermGetter($t, 'KR'),
  },
  'LI': {
    message: $t('billing.tax_promote_vat_cat1'),
    prefix: 'MWST',
    placeholder: $t('billing.vat_number'),
    validation: /^\d{5}$/,
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'LI'),
  },
  'MY': {
    message: $t('billing.tax_promote_my'),
    suffix: 'SST',
    placeholder: 'SST Number',
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'MY'),
  },
  'MX': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: checkMXBNNumber,
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'MX'),
  },
  'MD': {
    message: $t('billing.tax_number_promote.default'),
    placeholder: 'TIN',
    checkbox: $t('billing.tax_checkbox_md'),
    tooltip: $t('billing.mdMsgDesc'),
    validation: /^1\d{12}$/,
    taxTerm: taxTermGetter($t, 'MD'),
  },
  'NG': {
    message: $t('billing.ngMsg'),
    placeholder: $t('billing.tin_number'),
    validation: /^[0-9]{8}-[0-9]{4}$/,
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'NG'),
  },
  'NO': {
    message: $t('billing.tax_promote_no'),
    placeholder: $t('billing.mva_number'),
    suffix: 'MVA',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.tax_checkbox_desc_template', [$t('billing.tax_checkbox_desc_no')]),
    validation: /^\d{9}$/,
    taxTerm: taxTermGetter($t, 'NO'),
  },
  'OM': {
    message: $t('billing.vat_header_val_OM'),
    placeholder: $t('billing.vat_number'),
    prefix: 'OM',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message', [$t('billing.vat_checkout_popup_message_OM')]),
    validation: /^\d{10}$/,
    taxTerm: taxTermGetter($t, 'OM'),
  },
  'PH': {
    message: $t('billing.vat.msg_PH'),
    placeholder: 'TIN',
    checkbox: $t('billing.vat.non_business_customer'),
    tooltip: $t('billing.vat.tooltip_PH'),
    validation: /^(?:\d{3}-\d{3}-\d{3}(?:-\d{3})?|\d{9}|\d{12})$/,
    taxTerm: taxTermGetter($t, 'PH'),
  },
  'RU': {
    message: $t('billing.tax_promote.include_your_vat_number'),
    placeholder: $t('billing.vat_number'),
    prefix: 'RU',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_RU'),
    taxTerm: taxTermGetter($t, 'RU'),
  },
  'SA': {
    message: $t('billing.tax_promote_default'),
    placeholder: $t('billing.vat_number'),
    prefix: 'SA',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_cat2_checkbox_desc'),
    validation: checkSABNNumber,
    taxTerm: taxTermGetter($t, 'SA'),
  },
  'RS': {
    message: $t('billing.vat_header_val_RS'),
    placeholder: $t('billing.vat_number'),
    validation: /^\d{9}$/,
    taxExempt: false,
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'RS'),
  },
  'SG': {
    message: $t('billing.not_gst_tips'),
    placeholder: $t('billing.gst_number'),
    prefix: 'GST',
    checkbox: $t('billing.not_gst_registered'),
    tooltip: $t('billing.gst_checkbox_desc'),
    validation: checkSGBNNumber,
    taxTerm: taxTermGetter($t, 'SG'),
  },
  'ZA': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: checkZABNNumber,
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'ZA'),
  },
  'CH': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: window.checkVATNumber,
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'CH'),
  },
  'TW': {
    message: $t('billing.vat_header_val_TW'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_TW'),
    validation: /^\d{8}$/,
    taxTerm: taxTermGetter($t, 'TW'),
  },
  'TZ': {
    message: $t('billing.vat_header_val_TZ'),
    placeholder: 'VRN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_BD'),
    validation: /^\d{2}-\d{6}-[A-Z]$/,
    taxTerm: taxTermGetter($t, 'TZ'),
  },
  'TH': {
    message: $t('billing.vat.msg_th'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_cat2_checkbox_desc'),
    validation: /^\d{13}$/,
    taxTerm: taxTermGetter($t, 'TH'),
  },
  'TR': {
    message: $t('billing.tax_promote_tr'),
    placeholder: $t('billing.tax_input_placeholder'),
    prefix: 'VAT',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.tax_checkbox_desc_template', [$t('billing.tax_checkbox_desc_tr')]),
    validation: /^\d{10,11}$/,
    taxTerm: taxTermGetter($t, 'TR'),
  },
  'UG': {
    message: $t('billing.ugMsg'),
    placeholder: 'TIN',
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_KHM'),
    validation: /^\d{10}$/,
    taxTerm: taxTermGetter($t, 'UG'),
  },
  'UA': {
    message: $t('billing.vat_header_val_UKR'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message', [$t('billing.vat_checkout_popup_message_UKR')]),
    validation: /^\d{9}$|^\d{10}$|^\d{12}$/,
    taxTerm: taxTermGetter($t, 'UA'),
  },
  'UZ': {
    message: $t('billing.tax_promote_tr'),
    placeholder: $t('billing.vat_number'),
    checkbox: $t('billing.tax_checkbox_by'),
    tooltip: $t('billing.uzCheckboxMsg'),
    validation: /^\d{12}$/,
    isEmptiable: true,
    revert: true,
    taxTerm: taxTermGetter($t, 'UZ'),
  },
  'AE': {
    message: $t('billing.tax_promote_vat_cat1'),
    placeholder: $t('billing.vat_number'),
    validation: checkAEBNNumber,
    isEmptiable: true,
    taxTerm: taxTermGetter($t, 'AE'),
  },
  'VN': {
    message: $t('billing.vat_header_val_VNM'),
    placeholder: $t('billing.tax_input_placeholder'),
    checkbox: $t('billing.not_vat_registered'),
    tooltip: $t('billing.vat_checkout_popup_message_VNM'),
    validation: /^\d{10}$/,
    taxTerm: taxTermGetter($t, 'VN'),
  },
  'GB': {
    message: $t('billing.vat.GB_message'),
    tooltip: $t('billing.vat.UK_tootip'),
    validation: window.checkVATNumber,
    taxTerm: taxTermGetter($t, 'GB'),
  },
  'IC': {
    message: $t('billing.vat.msg_ic'),
    placeholder: 'NIF',
    prefix: 'ES',
    tooltip: $t('billing.vat.tooltip_ic'),
    validation: window.checkVATNumber,
    taxTerm: taxTermGetter($t, 'IC'),
  }
});

export const EU_DISCLAIM_COUNTRIES = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL','PT', 'RO', 'SK', 'SI', 'ES', 'SE','GB', 'IS', 'LI', 'NO', 'DE','IM']
export const EU_COUNTRIES = ['AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'SE', 'MC', 'IM', 'GB', 'IC'];
export const UIN_NEEDED_COUNTRIES = ['EG'];

export const ONLINE_VALIDATION_COUNTRIES = ['BD', 'CM', 'AU', 'UG', 'TW', 'BH', 'IS', 'RU', 'VN', 'SG', 'BY', 'CL', 'CO', 'NO', 'SA', 'OM', 'TH', 'IN', 'KR', 'EG', 'PE'];

export const getDefaultAddressInfo = () => {
  return {
    address1: '',
    address2: '',
    zip: '',
    city: '',
    state: '',
    country: '',
    countryId: '',
    phone: '',
    accountHq: '',
    companyName: '',
    employeeCount: '',
    usageType: '',
    countryName: '',
    vatNumber: '',
    notVatRegistered: false,
    doingBusinessAsName: '',
    businessType: '',
    otherBusinessType: '',
  }
}

const state = {
  addressInfo: getDefaultAddressInfo(),
  countryMap: {},
  ipLocation: safeJSONParse(window.ipLocation || '{}', {}),
  useCaseList: []
}

const getters = {
  addressInfo: (state) => state.addressInfo,
  countryMap: (state) => state.countryMap,
  useCaseList: (state) => state.useCaseList,
  countryList: state => {
    const countryMap = state.countryMap;
    return sortBy(Object.keys(countryMap)).reduce((result, label) => {
      result.push({label: label, value: countryMap[label]});
      return result;
    }, [])
  },
}

const mutations = {
  setCountryMap(state, countryMap) {
    state.countryMap = countryMap;
  },
  setAddressInfo(state, data) {
    state.addressInfo = data;
  },
  setUsageType(state,data) {
    state.addressInfo.usageType =  data
  },
  setUseCaseList(state,data) {
    state.useCaseList=  data
  },
}

const actions = {
  async [ADDRESS_ACTION_TYPES.fetchCountryMap]({commit}) {
    try {
      let data = await getCountryJson();
      // data's format is {countryName:countryId}, like {china: cn}
      // need do filter, restrict some forbidden countries
      data = omitBy(data, function (value){
        return RESTRICTED_COUNTRIES.includes(value);
      })
      commit('setCountryMap', data);
    } catch (e) {
      console.error('get country map error');
    }
  },
  updateAddressInfo({commit}, {key, value}) {
    commit('setAddressInfo', {[key]: value});
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}
